import {useEffect, type ComponentType} from 'react';
import {Navigate, Outlet, useLocation, useParams} from 'react-router-dom';
import {useDocument} from 'react-firebase-hooks/firestore';
import type {FirestoreError} from 'firebase/firestore';
import {getCourseDocumentRef} from '@cip/core/firebase/client';
import {ErrorPage} from 'components/errors/ErrorPage';
import {Loading} from 'components/loading/Loading';
import {
  checkIsParentCourseId,
  getApplicationTypeFromPath,
  getAppSplashRoute,
  getParentCourseId,
  PAI_PARENT_COURSE_ID,
} from 'course/application/general/utils/apputils';
import {isNullOrUndefined} from 'utils/general';
import {useApplicationJoinData} from 'course/application/layout/ApplicationJoinLayout';
import type {ApplicationType} from 'course/application/firebase/models/applicationData';

/**
 * Entry gate for the application short link, `/apply/:targetCourseId/student`
 * or `/apply/:targetCourseId/sl`.
 *
 * The course-wide providers are already mounted above this route by
 * `CourseScopeLayout` (the pathless layout in Router.tsx that resolves both
 * this subtree and `/:courseId/…` to the same course scope), so this gate
 * only decides who may live at the short URL:
 *
 * - PAI-parented courses always render here — the short link is their real,
 *   persistent address.
 * - Any other course opts in by setting `applyShortLinkEnabled` on its course
 *   document (an admin checkbox in the course's Admin editor).
 * - Courses that have not opted in redirect to their long-form canonical
 *   splash route (`/:parentCourseId/join|applyteach/:targetCourseId`),
 *   unchanged from before this route existed.
 * - Broken links render a not-found error page instead of redirecting: a
 *   target that names a parent course (e.g. `/apply/pai/student` — parent
 *   courses host application materials, they don't accept applicants) or a
 *   target whose course document doesn't exist.
 * - A course document that could not be read renders a connection error page,
 *   so a blocked network is not reported as a missing course.
 *
 * Reading the flag costs one course-document read (globally readable, so it
 * works for logged-out applicants; the flow re-reads the same doc after
 * sign-in, which Firestore serves from cache). PAI skips the wait entirely.
 * Redirects carry the URL's query string so referral codes (`?r=…`) survive.
 *
 * @return The matched child route (short link enabled), a loading screen
 *   while the flag is being read, the redirect (short link disabled), or an
 *   error page for a broken link (no target segment, a parent course as the
 *   target, or a course that doesn't exist) or an unreadable course document.
 */
export const ApplyRouteGate = () => {
  const {targetCourseId} = useParams();
  const location = useLocation();

  const [courseSnapshot, courseDataLoading, courseDataError] = useDocument(
    targetCourseId ? getCourseDocumentRef({courseId: targetCourseId}) : null,
  );
  const courseDocReadState = getCourseDocReadState({
    snapshot: courseSnapshot,
    error: courseDataError,
  });

  useEffect(() => {
    if (courseDocReadState !== 'unreachable') {
      return;
    }
    console.warn('[ApplyRouteGate] course document unreachable', {
      targetCourseId,
      errorCode: courseDataError?.code,
    });
  }, [courseDocReadState, targetCourseId, courseDataError?.code]);

  if (!targetCourseId) {
    return (
      <ErrorPage msg="Unknown application link: missing target course" />
    );
  }

  // A parent course hosts application materials; it is never itself an
  // application target, so a link naming one is broken. Static check, so it
  // errors immediately without waiting on the data read.
  if (checkIsParentCourseId(targetCourseId)) {
    return (
      <ErrorPage
        msg={`Unknown application link: "${targetCourseId}" is not a course you can apply to`}
      />
    );
  }

  const parentCourseId = getParentCourseId(targetCourseId);

  // PAI's short link must never depend on a data read: render immediately.
  if (parentCourseId === PAI_PARENT_COURSE_ID) {
    return <Outlet />;
  }

  if (courseDataLoading) {
    return <Loading />;
  }

  if (courseDocReadState === 'unreachable') {
    return (
      <ErrorPage msg="We couldn't reach Code in Place to load this application. Check your internet connection and reload the page." />
    );
  }

  const courseData = courseSnapshot?.data();
  if (isNullOrUndefined(courseData)) {
    return (
      <ErrorPage
        msg={`Unknown application link: there is no course "${targetCourseId}"`}
      />
    );
  }

  if (courseData.applyShortLinkEnabled) {
    return <Outlet />;
  }

  const role = getApplicationTypeFromPath(location.pathname) ?? 'student';
  return (
    <Navigate
      to={`${getAppSplashRoute(targetCourseId, role, parentCourseId)}${location.search}`}
      replace
    />
  );
};

/**
 * Outcome of reading a course document through a snapshot listener.
 *
 * - `found`: the document exists.
 * - `missing`: the server confirmed the document does not exist.
 * - `unreachable`: the listener errored, or the SDK gave up reaching the
 *   backend and answered from its local cache.
 * - `pending`: no snapshot or error yet.
 */
export type CourseDocReadState = 'found' | 'missing' | 'unreachable' | 'pending';

/**
 * Classifies a course-document listener result.
 *
 * An unreachable backend does not surface as a listener error: once the
 * Firestore SDK marks itself offline, it answers with a cache snapshot whose
 * document does not exist, so only `metadata.fromCache` tells that apart from
 * a real miss.
 *
 * @param snapshot - The latest snapshot. When omitted, the result is
 *   `unreachable` if `error` is set and `pending` otherwise.
 * @param error - The listener error. When omitted, only `snapshot` decides.
 * @return The read state.
 * @throws Never.
 */
export function getCourseDocReadState({
  snapshot,
  error,
}: {
  snapshot?: {exists: () => boolean; metadata: {fromCache: boolean}};
  error?: Pick<FirestoreError, 'code'>;
}): CourseDocReadState {
  if (!isNullOrUndefined(error)) {
    return 'unreachable';
  }
  if (isNullOrUndefined(snapshot)) {
    return 'pending';
  }
  if (snapshot.exists()) {
    return 'found';
  }
  return snapshot.metadata.fromCache ? 'unreachable' : 'missing';
}

/**
 * Wraps a long-form application splash component so that, when the target
 * course is served at the short link (a PAI-parented course, or any course
 * with `applyShortLinkEnabled` set on its course document), visiting the old
 * canonical route (`/:parentCourseId/join/:targetCourseId` or
 * `/:parentCourseId/applyteach/:targetCourseId`) redirects to the short-link
 * URL instead of rendering — so links shared before the short link existed
 * still land correctly. Every other course keeps rendering `component`
 * exactly as before this existed.
 *
 * Rendered as the index route inside `ApplicationJoinLayout`, so the target
 * course's document is read from {@link useApplicationJoinData} (no extra
 * fetch). Redirects carry the query string so referral codes survive.
 *
 * @param role - Which application flow this splash belongs to; selects the
 *   short-link segment (`student` or `sl`) to redirect to.
 * @param component - The long-form splash component to render for courses not
 *   served at the short link (e.g. `StudentApplication`,
 *   `SectionLeaderApplication`).
 * @return The redirect (short link enabled), a loading screen while the flag
 *   is being read, or the rendered `component` (every other course).
 */
export const LegacyApplicationSplashRedirect = ({
  role,
  component: Component,
}: {
  role: ApplicationType;
  component: ComponentType;
}) => {
  const {courseId, targetCourseId} = useParams();
  const location = useLocation();
  const {courseData, courseDataLoading} = useApplicationJoinData();

  const shortLinkRoute = `/apply/${targetCourseId}/${role}${location.search}`;

  if (courseId === PAI_PARENT_COURSE_ID) {
    return <Navigate to={shortLinkRoute} replace />;
  }

  if (courseDataLoading) {
    return <Loading />;
  }

  if (courseData?.applyShortLinkEnabled) {
    return <Navigate to={shortLinkRoute} replace />;
  }

  return <Component />;
};
